import type { Rng } from '../../core/rng';
import { LIFE_ENERGY_MAX, LIFE_ENERGY_REGEN, type AccountSave } from '../../game/account';
import type { LifeSkill } from '../../game/character';
import type { InteractableDef } from '../../zones/types';

/**
 * Life-skill rules (research 02 §8, scaled down in §11.2/§11.5): which node
 * uses which skill and mini-game, Life Energy costs, XP, yield and rarity
 * tables by skill level. Everything here is pure and unit tested.
 */

export type NodeKind = 'herb' | 'timber' | 'ore' | 'fish' | 'dig';
export type MiniGameKind = 'ringtap' | 'holdbar' | 'fishing' | 'dig';

export interface NodeDef {
  kind: NodeKind;
  skill: LifeSkill;
  /** What the player is working on ("Wild Herbs"). */
  name: string;
  game: MiniGameKind;
  /** Common and rare material ids (src/game/items.ts). */
  common: string;
  rare: string;
  /** Minutes before a gathered node reappears (0 = never depletes). */
  respawnMin: number;
  /** Hero clip looped while working. */
  anim: 'Gather' | 'Fish';
}

export const NODES: Record<NodeKind, NodeDef> = {
  herb: { kind: 'herb', skill: 'foraging', name: 'Wild Herbs', game: 'ringtap', common: 'herb', rare: 'herb_rare', respawnMin: 3, anim: 'Gather' },
  timber: { kind: 'timber', skill: 'logging', name: 'Timber', game: 'holdbar', common: 'timber', rare: 'timber_rare', respawnMin: 4, anim: 'Gather' },
  ore: { kind: 'ore', skill: 'mining', name: 'Ore Vein', game: 'holdbar', common: 'ore_raw', rare: 'ore_rare', respawnMin: 5, anim: 'Gather' },
  fish: { kind: 'fish', skill: 'fishing', name: 'Fishing Spot', game: 'fishing', common: 'fish', rare: 'fish_rare', respawnMin: 0, anim: 'Fish' },
  dig: { kind: 'dig', skill: 'excavating', name: 'Dig Site', game: 'dig', common: 'relic_fragment', rare: 'relic_rare', respawnMin: 6, anim: 'Gather' },
};

export interface SkillInfo {
  name: string;
  /** Base Life Energy per action (before Haven research). */
  energy: number;
  /** Base life-skill XP per action at quality 1. */
  xp: number;
  /** Accent colour for UI. */
  hue: number;
}

export const SKILL_INFO: Record<LifeSkill, SkillInfo> = {
  foraging: { name: 'Foraging', energy: 20, xp: 30, hue: 0x7bd66b },
  logging: { name: 'Logging', energy: 25, xp: 36, hue: 0xc08040 },
  mining: { name: 'Mining', energy: 25, xp: 36, hue: 0x9ab0c8 },
  fishing: { name: 'Fishing', energy: 30, xp: 40, hue: 0x5ab8e0 },
  hunting: { name: 'Hunting', energy: 10, xp: 20, hue: 0xd08050 },
  excavating: { name: 'Excavating', energy: 40, xp: 50, hue: 0xd0a060 },
};

/** Resolves the node an interactable represents (gather nodes carry `data.node`). */
export function nodeFor(it: Pick<InteractableDef, 'kind' | 'data'>): NodeDef | null {
  if (it.kind === 'fish') return NODES.fish;
  if (it.kind === 'dig') return NODES.dig;
  if (it.kind !== 'gather') return null;
  const n = it.data?.node;
  if (n === 'herb' || n === 'timber' || n === 'ore') return NODES[n];
  const skill = it.data?.skill;
  if (skill === 'logging') return NODES.timber;
  if (skill === 'mining') return NODES.ore;
  return NODES.herb;
}

// ---------------------------------------------------------------- energy

/** Life Energy an action costs after the Haven's "Efficient Tools" reduction (0..0.9). */
export function energyCost(skill: LifeSkill, reduction = 0): number {
  const r = Math.max(0, Math.min(0.9, reduction));
  return Math.max(1, Math.round(SKILL_INFO[skill].energy * (1 - r)));
}

/** Gives energy back (an aborted gather), never above the cap. */
export function refundEnergy(a: Pick<AccountSave, 'lifeEnergy'>, n: number): void {
  a.lifeEnergy = Math.min(LIFE_ENERGY_MAX, a.lifeEnergy + Math.max(0, n));
}

/** Whole minutes until Life Energy is full again (0 when full). */
export function minutesToFull(energy: number): number {
  return energy >= LIFE_ENERGY_MAX ? 0 : Math.ceil((LIFE_ENERGY_MAX - energy) / LIFE_ENERGY_REGEN);
}

// ---------------------------------------------------------------- XP

/** Life-skill XP for one action; performance (0..1) scales it between 60% and 140%. */
export function lifeXpFor(skill: LifeSkill, quality: number): number {
  const q = Math.max(0, Math.min(1, quality));
  return Math.round(SKILL_INFO[skill].xp * (0.6 + 0.8 * q));
}

// ---------------------------------------------------------------- yields

export interface YieldRow {
  /** Inclusive range of common materials before the performance bonus. */
  common: [number, number];
  /** Base chance of a rare material before performance/research bonuses. */
  rareChance: number;
}

const COMMON_BY_LEVEL: [number, number][] = [
  [1, 2], [1, 2], [2, 3], [2, 3], [2, 4], [3, 4], [3, 4], [3, 5], [4, 5], [4, 6],
];

/** Yield table for life-skill levels 1..10 (clamped). */
export function yieldTable(level: number): YieldRow {
  const lvl = Math.max(1, Math.min(10, Math.floor(level)));
  return { common: COMMON_BY_LEVEL[lvl - 1] ?? [1, 2], rareChance: 0.04 + 0.025 * (lvl - 1) };
}

/** Final rare chance: level base + up to +15% for a perfect mini-game + research bonus (cap 75%). */
export function rareChanceFor(level: number, quality: number, bonus = 0): number {
  const q = Math.max(0, Math.min(1, quality));
  return Math.min(0.75, yieldTable(level).rareChance + q * 0.15 + Math.max(0, bonus));
}

export interface YieldOpts {
  /** Guarantees the rare material (a hooked rare fish, an unearthed relic). */
  forceRare?: boolean;
  /** Set false when the mini-game already decided rarity (fishing decides at the hook). */
  rareRoll?: boolean;
  /** Extra rare chance (Haven "Surveyor's Lore"). */
  rareBonus?: number;
}

export interface ItemCount {
  id: string;
  count: number;
}

/**
 * Rolls the materials of one successful gather. Higher levels raise both the
 * common count and the rare chance; mini-game performance (0..1) adds up to
 * +2 commons and +15% rare chance, and a near-perfect run can double the rare.
 */
export function rollYield(node: NodeDef, level: number, quality: number, rng: Rng, opts: YieldOpts = {}): ItemCount[] {
  const q = Math.max(0, Math.min(1, quality));
  const row = yieldTable(level);
  const common = rng.int(row.common[0], row.common[1]) + Math.round(q * 2);
  const out: ItemCount[] = [{ id: node.common, count: Math.max(1, common) }];
  const rare = opts.forceRare || (opts.rareRoll !== false && rng.next() < rareChanceFor(level, q, opts.rareBonus ?? 0));
  if (rare) out.push({ id: node.rare, count: q >= 0.9 && rng.next() < 0.25 ? 2 : 1 });
  return out;
}

/** Hides carved from a slain beast: more with hunting level; named beasts give extra. */
export function huntYield(level: number, grade: string, rng: Rng): number {
  const lvl = Math.max(1, Math.min(10, Math.floor(level)));
  const base = rng.int(1, 2) + Math.floor(lvl / 4);
  return grade === 'named' || grade === 'boss' ? base + 3 : grade === 'elite' ? base + 1 : base;
}
